import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Tabs from 'components/application/Tabs';

import CourseSidePanelDetailsTab from 'components/courses/sidepanel/CourseSidePanelDetailsTab';
import CourseSidePanelPersonnelTab from 'components/courses/sidepanel/CourseSidePanelPersonnelTab';
import CourseSidePanelRolesTab from 'components/courses/sidepanel/CourseSidePanelRolesTab';

import { resourceShape } from 'components/helpers/serialisableResources';

import CourseSidePanelOverridesTab from './CourseSidePanelOverridesTab';

export default function CourseSidePanelShow({
  activeTab,
  assignableRoles,
  autoEnrolmentConfiguration,
  currentCourse,
  domainCourse,
  eLearningCourse,
  isLoadMorePersonnelActive,
  isOverridesRemoveModalOpen,
  isSidePanelOpen,
  loadMoreRef,
  onOverrideRemove,
  onOverrideRemoveModalClose,
  onOverrideRemoveSuccess,
  onTabClick,
  originalCourse,
  personnel,
  selectedRequirementPriorityIds,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelShow',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  // get role name for selected courseCompanyRoles
  let rolesCollection = [];
  if (assignableRoles.length > 0) {
    rolesCollection = currentCourse.courseCompanyRoles.map((ccr) => {
      const role = assignableRoles.find(
        (role) => role.id === ccr.companyRoleId,
      );
      return { rolePosition: role.attributes.position, required: ccr.required };
    });
  }

  return (
    <div className='popup__body-show tw-p-0'>
      <Tabs
        activeTab={activeTab}
        modifiers={['panelled']}
        navClassName='tw-bg-grey-050'
        onTabClick={onTabClick}
      >
        <CourseSidePanelDetailsTab
          course={domainCourse}
          eLearningCourse={eLearningCourse}
          id='details'
          key='detailsTab'
          label={i18nTShared('details')}
          originalCourse={originalCourse}
        />
        <CourseSidePanelPersonnelTab
          autoEnrolmentConfiguration={autoEnrolmentConfiguration}
          course={currentCourse}
          disabled={!personnel.loaded || personnel.collection.length === 0}
          domainCourse={domainCourse}
          eLearningCourse={eLearningCourse}
          id='personnel'
          isLoadMorePersonnelActive={isLoadMorePersonnelActive}
          key='personnelTab'
          label={i18nTShared('personnel')}
          loadMoreRef={loadMoreRef}
          personnel={personnel}
          tooltipText={i18nT('personnelTab.tooltipText')}
        />
        <CourseSidePanelRolesTab
          courseCompanyRoles={rolesCollection}
          disabled={currentCourse.courseCompanyRoles.length === 0}
          id='roles'
          key='rolesTab'
          label={i18nTShared('roles')}
          tooltipText={i18nT('rolesTab.tooltipText')}
        />
        <CourseSidePanelOverridesTab
          courseId={currentCourse.id}
          courseName={currentCourse.name}
          disabled={domainCourse.meta.requirementPriorityCount === 0}
          flag={
            domainCourse.meta.requirementPriorityCount > 0 && (
              <span className='tw-relative tw--top-0.5 tw-ml-1 tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050 tw-font-inter tw-text-xs tw-font-semibold'>
                {domainCourse.meta.requirementPriorityCount}
              </span>
            )
          }
          id='overrides'
          isOverridesRemoveModalOpen={isOverridesRemoveModalOpen}
          isSidePanelOpen={isSidePanelOpen}
          key='overridesTab'
          label={i18nTShared('overrides')}
          onOverrideRemove={onOverrideRemove}
          onOverrideRemoveModalClose={onOverrideRemoveModalClose}
          onOverrideRemoveSuccess={onOverrideRemoveSuccess}
          selectedRequirementPriorityIds={selectedRequirementPriorityIds}
          tooltipText={i18nT('overridesTab.tooltipText')}
        />
      </Tabs>
    </div>
  );
}

CourseSidePanelShow.propTypes = {
  currentCourse: PropTypes.object.isRequired,
  domainCourse: resourceShape('course').isRequired,
  originalCourse: resourceShape('originalCourse'),
  eLearningCourse: resourceShape('eLearningCourse'),
  autoEnrolmentConfiguration: resourceShape('autoEnrolmentConfiguration'),
  assignableRoles: PropTypes.array.isRequired,
  personnel: PropTypes.object.isRequired,
  isLoadMorePersonnelActive: PropTypes.bool.isRequired,
  isOverridesRemoveModalOpen: PropTypes.bool.isRequired,
  isSidePanelOpen: PropTypes.bool.isRequired,
  activeTab: PropTypes.string.isRequired,
  onOverrideRemoveModalClose: PropTypes.func.isRequired,
  onOverrideRemoveSuccess: PropTypes.func.isRequired,
  onTabClick: PropTypes.func.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  selectedRequirementPriorityIds: PropTypes.array.isRequired,
};
